import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as MuiIcons from '@mui/icons-material';
import CentralCalls from '../../centralCalls';

const TYPE_OPTIONS = ['table', 'line', 'bar', 'pie', 'single', 'titlewidget', 'phasetypefilter', 'datefilter'];

const POPULAR_ICON_NAMES = [
	'Home',
	'Settings',
	'Search',
	'Person',
	'People',
	'AccountCircle',
	'Notifications',
	'Favorite',
	'Star',
	'Dashboard',
	'Analytics',
	'Assessment',
	'BarChart',
	'ShowChart',
	'PieChart',
	'TableChart',
	'InsertChart',
	'TrendingUp',
	'TrendingDown',
	'Timeline',
	'Description',
	'Article',
	'Summarize',
	'ReceiptLong',
	'FactCheck',
	'FileCopy',
	'Folder',
	'FolderOpen',
	'Save',
	'Download',
	'Upload',
	'Print',
	'Edit',
	'Delete',
	'Add',
	'Remove',
	'Check',
	'Close',
	'DoneAll',
	'Build',
	'Construction',
	'Handyman',
	'ManageAccounts',
	'AdminPanelSettings',
	'SupervisorAccount',
	'Badge',
	'Business',
	'Domain',
	'CorporateFare',
	'LocationOn',
	'Map',
	'Place',
	'PinDrop',
	'Route',
	'Today',
	'CalendarMonth',
	'Event',
	'Schedule',
	'AccessTime',
	'DateRange',
	'PendingActions',
	'Task',
	'Checklist',
	'Rule',
	'Assignment',
	'AssignmentTurnedIn',
	'EventNote',
	'AutoGraph',
	'QueryStats',
	'Insights',
	'CompareArrows',
	'SwapHoriz',
	'Sync',
	'Refresh',
	'History',
	'Undo',
	'Redo',
	'Visibility',
	'VisibilityOff',
	'FilterList',
	'Tune',
	'Sort',
	'ViewList',
	'ViewModule',
	'GridView',
	'ViewAgenda',
	'Widgets',
	'Extension',
	'Category',
	'Inventory2',
	'LocalShipping',
	'ShoppingCart',
	'PointOfSale',
	'Payments',
	'AttachMoney',
	'RequestQuote',
	'AccountBalanceWallet',
	'CreditCard',
	'Receipt',
	'Sell',
	'PriceCheck',
	'Percent',
	'Paid',
	'MonetizationOn',
	'Engineering',
	'Architecture',
	'PrecisionManufacturing',
	'Factory',
	'Warehouse',
	'Science',
	'Biotech',
	'MedicalServices',
	'Plumbing',
	'ElectricalServices',
	'WaterDrop',
	'Opacity',
	'Bolt',
	'Power',
	'DeviceHub',
	'Dns',
	'Storage',
	'Cloud',
	'CloudDone',
	'CloudUpload',
	'CloudDownload',
	'Laptop',
	'DesktopWindows',
	'Phone',
	'Tablet',
	'QrCode',
	'QrCodeScanner',
	'Wifi',
	'Router',
	'Security',
	'Lock',
	'LockOpen',
	'VpnKey',
	'Key',
	'Shield',
	'Warning',
	'Error',
	'Info',
	'Report',
	'BugReport',
	'Help',
	'SupportAgent',
	'Chat',
	'Forum',
	'Email',
	'Mail',
	'Call',
	'ContactPhone',
	'ContactMail',
	'AlternateEmail',
	'Groups',
	'Group',
	'PersonAdd',
	'HowToReg',
	'VerifiedUser',
	'WorkspacePremium',
	'School',
	'MenuBook',
	'Language',
	'Public',
	'Translate',
	'Flag',
	'OutlinedFlag',
	'EmojiEvents',
	'MilitaryTech',
	'Celebration',
	'Campaign',
	'Mic',
	'VolumeUp',
	'HeadsetMic',
	'Videocam',
	'CameraAlt',
	'Image',
	'Photo',
	'Slideshow',
	'Palette',
	'Brush',
	'Draw',
	'Style',
	'AutoFixHigh',
	'RocketLaunch',
	'FlightTakeoff',
	'DirectionsCar',
	'LocalTaxi',
	'Train',
	'DirectionsBus',
	'DirectionsBoat',
	'TwoWheeler',
	'PedalBike',
	'Park',
	'Landscape',
	'Yard',
	'Nature',
	'WbSunny',
	'NightsStay',
	'Thermostat',
	'AcUnit',
	'Whatshot',
	'LocalFireDepartment',
	'HomeRepairService',
	'Roofing',
	'DoorFront',
	'Chair',
	'Bed',
	'Bathtub',
	'Kitchen',
	'Weekend',
	'LocalLaundryService',
	'CleanHands',
	'HealthAndSafety',
	'MonitorHeart',
	'Vaccines',
	'ConstructionOutlined',
	'Tooltip',
	'Pin',
	'Code',
	'DataObject',
	'IntegrationInstructions',
	'Terminal',
	'Api',
	'Hub',
	'Memory',
	'Psychology',
	'Lightbulb',
	'TipsAndUpdates',
	'AutoAwesome',
	'BoltOutlined',
	'CheckCircle',
	'Cancel',
	'HourglassBottom',
	'PlayArrow',
	'Pause',
	'Stop',
	'FastForward',
	'FastRewind',
	'SkipNext',
	'SkipPrevious',
	'FirstPage',
	'LastPage',
];

const ALL_ICON_NAMES = Object.keys(MuiIcons).filter((name) => /^[A-Z]/.test(name) && !!MuiIcons[name]);
const CURATED_ICON_NAMES = Array.from(new Set([...POPULAR_ICON_NAMES, ...ALL_ICON_NAMES])).slice(0, 200);

const ICON_OPTIONS = CURATED_ICON_NAMES.map((name) => ({
	value: name,
	Icon: MuiIcons[name],
})).filter((option) => !!option.Icon);

function parseJsonValue(value) {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function normalizeProcedureResult(raw) {
	if (Array.isArray(raw) && raw.length === 1 && raw[0] && typeof raw[0] === 'object' && 'json' in raw[0]) {
		return parseJsonValue(raw[0].json);
	}

	if (raw && typeof raw === 'object' && 'json' in raw) {
		return parseJsonValue(raw.json);
	}

	return raw;
}

function extractRowsFromData(data) {
	if (Array.isArray(data)) {
		return data.filter((row) => row && typeof row === 'object' && !Array.isArray(row));
	}
	if (data && typeof data === 'object' && Array.isArray(data.rows)) {
		return data.rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row));
	}
	return [];
}

function normalizeStringList(values) {
	if (!Array.isArray(values)) {
		return [];
	}

	const seen = new Set();
	const normalized = [];
	values.forEach((value) => {
		if (typeof value !== 'string') {
			return;
		}
		const trimmed = value.trim();
		if (!trimmed || seen.has(trimmed)) {
			return;
		}
		seen.add(trimmed);
		normalized.push(trimmed);
	});

	return normalized;
}

function parseStringListInput(value) {
	if (typeof value !== 'string' || !value.trim()) {
		return [];
	}

	return normalizeStringList(
		value
			.split(/[\n,]/g)
			.map((part) => part.trim())
			.filter(Boolean)
	);
}

function listToMultiline(values) {
	return normalizeStringList(values).join('\n');
}

function parseIntegerOrNull(value) {
	if (value === null || value === undefined) {
		return null;
	}

	const trimmed = String(value).trim();
	if (!trimmed) {
		return null;
	}

	if (!/^[-+]?\d+$/.test(trimmed)) {
		return null;
	}

	return Number.parseInt(trimmed, 10);
}

function ReportWidgetEdit({ widget, onChange }) {
	const [form, setForm] = useState({
		headerLabel: '',
		subHeaderLabel: '',
		notes: '',
		procedureName: '',
		type: 'table',
		filterVariableName: '',
		dateDefaultOffset: '',
		icon: '',
		backgroundColor: '',
		columnOrder: [],
		hideSearchbar: false,
	});
	const [columnOrderInput, setColumnOrderInput] = useState('');
	const [detectedColumns, setDetectedColumns] = useState([]);
	const [loadingColumns, setLoadingColumns] = useState(false);
	const [columnsError, setColumnsError] = useState('');
	const [procedureOptions, setProcedureOptions] = useState([]);
	const [loadingProcedures, setLoadingProcedures] = useState(false);
	const [iconPickerOpen, setIconPickerOpen] = useState(false);
	const iconPickerRef = useRef(null);

	useEffect(() => {
		const initialColumnOrder = normalizeStringList(widget?.columnOrder || widget?.data?.columnOrder || []);
		setForm({
			headerLabel: widget?.headerLabel || widget?.data?.headerLabel || '',
			subHeaderLabel: widget?.subHeaderLabel || widget?.data?.subHeaderLabel || widget?.data?.subHeader || '',
			notes: widget?.notes || widget?.data?.notes || '',
			procedureName: widget?.procedureName || '',
			type: widget?.type || 'table',
			filterVariableName:
				widget?.filterVariableName || widget?.filterParamKey || widget?.data?.filterVariableName || widget?.data?.filterParamKey || '',
			dateDefaultOffset:
				widget?.dateDefaultOffset === null || widget?.dateDefaultOffset === undefined
					? widget?.data?.dateDefaultOffset === null || widget?.data?.dateDefaultOffset === undefined
						? ''
						: String(widget?.data?.dateDefaultOffset)
					: String(widget?.dateDefaultOffset),
			icon: widget?.icon || '',
			backgroundColor: widget?.backgroundColor || '',
			columnOrder: initialColumnOrder,
			hideSearchbar: widget?.hideSearchbar || false,
		});
		setColumnOrderInput(listToMultiline(initialColumnOrder));
	}, [widget]);

	useEffect(() => {
		const handleOutsideClick = (event) => {
			if (!iconPickerRef.current || iconPickerRef.current.contains(event.target)) {
				return;
			}
			setIconPickerOpen(false);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	useEffect(() => {
		setDetectedColumns([]);
		setLoadingColumns(false);
		setColumnsError('');

		if (form.type !== 'table' || !form.procedureName) {
			return;
		}

		let isActive = true;
		const loadColumns = async () => {
			setLoadingColumns(true);
			setColumnsError('');
			try {
				const raw = await CentralCalls.callStoredProcedureWithParams(form.procedureName, {});
				if (!isActive) {
					return;
				}

				const normalized = normalizeProcedureResult(raw);
				const wrappedData = normalized && typeof normalized === 'object' && !Array.isArray(normalized) ? normalized.data : undefined;
				const dataset = wrappedData ?? normalized;
				const rows = extractRowsFromData(dataset);
				setDetectedColumns(rows.length ? Object.keys(rows[0]) : []);
			} catch (error) {
				if (!isActive) {
					return;
				}
				setDetectedColumns([]);
				setColumnsError('Could not detect columns from this procedure.');
				console.error('Failed to detect table columns', error);
			} finally {
				if (isActive) {
					setLoadingColumns(false);
				}
			}
		};

		void loadColumns();

		return () => {
			isActive = false;
		};
	}, [form.type, form.procedureName]);

	useEffect(() => {
		let isMounted = true;

		const loadProcedureOptions = async () => {
			setLoadingProcedures(true);
			try {
				const rows = await CentralCalls.tableSelect('reporting_procedure', {});
				if (!isMounted) {
					return;
				}

				const options = (Array.isArray(rows) ? rows : [])
					.filter((row) => typeof row?.procedure_name === 'string' && row.procedure_name.startsWith('reporting_'))
					.map((row) => ({
						value: row.procedure_name,
						label: row.display_name || row.procedure_name,
					}));

				setProcedureOptions(options);
			} catch (error) {
				if (isMounted) {
					setProcedureOptions([]);
				}
				console.error('Failed to load reporting procedures', error);
			} finally {
				if (isMounted) {
					setLoadingProcedures(false);
				}
			}
		};

		loadProcedureOptions();

		return () => {
			isMounted = false;
		};
	}, []);

	const update = (key, value) => {
		const next = { ...form, [key]: value };
		setForm(next);
		const nextDateDefaultOffset = parseIntegerOrNull(next.dateDefaultOffset);

		if (typeof onChange === 'function') {
			onChange({
				...widget,
				headerLabel: next.headerLabel,
				subHeaderLabel: next.subHeaderLabel,
				notes: next.notes,
				procedureName: next.procedureName,
				type: next.type,
				filterVariableName: next.filterVariableName,
				dateDefaultOffset: nextDateDefaultOffset,
				icon: next.icon,
				backgroundColor: next.backgroundColor,
				columnOrder: next.columnOrder,
				hideSearchbar: next.hideSearchbar,
				data: {
					...(widget?.data || {}),
					headerLabel: next.headerLabel,
					subHeaderLabel: next.subHeaderLabel,
					notes: next.notes,
					filterVariableName: next.filterVariableName,
					dateDefaultOffset: nextDateDefaultOffset,
					columnOrder: next.columnOrder,
				},
			});
		}
	};

	const filteredIcons = useMemo(() => {
		const query = (form.icon || '').trim().toLowerCase();
		if (!query) {
			return ICON_OPTIONS;
		}
		const matches = ICON_OPTIONS.filter((option) => option.value.toLowerCase().includes(query));
		return matches.length > 0 ? matches : ICON_OPTIONS;
	}, [form.icon]);

	return (
		<div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-auto p-2">
				<label className="space-y-1">
					<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Header</span>
					<input
						type="text"
						value={form.headerLabel}
						onChange={(event) => update('headerLabel', event.target.value)}
						className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
					/>
				</label>

				<label className="space-y-1">
					<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Sub Header</span>
					<input
						type="text"
						value={form.subHeaderLabel}
						onChange={(event) => update('subHeaderLabel', event.target.value)}
						className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
					/>
				</label>

				<label className="space-y-1">
					<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Notes</span>
					<textarea
						value={form.notes}
						onChange={(event) => update('notes', event.target.value)}
						rows={3}
						className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
					/>
				</label>

				<label className="space-y-1">
					<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Stored Procedure</span>
					<select
						value={form.procedureName}
						onChange={(event) => update('procedureName', event.target.value)}
						className="h-8 w-full rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
					>
						<option value="">{loadingProcedures ? 'Loading procedures...' : 'Select procedure'}</option>
						{procedureOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
						{form.procedureName && !procedureOptions.some((option) => option.value === form.procedureName) ? (
							<option value={form.procedureName}>{form.procedureName}</option>
						) : null}
					</select>
				</label>

				<label className="space-y-1">
					<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Widget Type</span>
					<select
						value={form.type}
						onChange={(event) => update('type', event.target.value)}
						className="h-8 w-full rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
					>
						{TYPE_OPTIONS.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</label>

				{form.type.endsWith('filter') ? (
					<label className="space-y-1">
						<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Filter Variable Name</span>
						<input
							type="text"
							value={form.filterVariableName}
							onChange={(event) => update('filterVariableName', event.target.value)}
							placeholder={form.type === 'datefilter' ? 'date' : 'phaseTypeNameId'}
							className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
						/>
					</label>
				) : null}

				{form.type === 'datefilter' ? (
					<label className="space-y-1">
						<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Date Default Offset (days)</span>
						<input
							type="number"
							step="1"
							value={form.dateDefaultOffset}
							onChange={(event) => update('dateDefaultOffset', event.target.value)}
							placeholder="0 for today, 7 for next week, -7 for last week"
							className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
						/>
						<span className="text-[10px] text-slate-500">Leave blank to skip sending a default date.</span>
					</label>
				) : null}

				{form.type === 'table' ? (
					<>
						<label className="space-y-1">
							<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Column Order</span>
							<textarea
								value={columnOrderInput}
								onChange={(event) => {
									const nextInput = event.target.value;
									setColumnOrderInput(nextInput);
									update('columnOrder', parseStringListInput(nextInput));
								}}
								placeholder="One column per line or comma separated"
								rows={4}
								className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
							/>
							{loadingColumns ? (
								<span className="text-[10px] text-slate-500">Detecting dataset columns...</span>
							) : null}
							{!loadingColumns && detectedColumns.length ? (
								<span className="text-[10px] text-slate-500">
									Detected columns: {detectedColumns.join(', ')}
								</span>
							) : null}
							{!loadingColumns && !detectedColumns.length && columnsError ? (
								<span className="text-[10px] text-amber-700">{columnsError}</span>
							) : null}
						</label>

						<label className="flex items-center gap-2 space-y-1">
							<input
								type="checkbox"
								checked={form.hideSearchbar}
								onChange={(event) => update('hideSearchbar', event.target.checked)}
								className="h-4 w-4 rounded border-slate-300 text-slate-700 outline-none transition focus:ring-2 focus:ring-slate-200"
							/>
							<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Hide Searchbar</span>
						</label>
					</>
				) : null}

				<div className="grid grid-cols-2 gap-2">
					<label className="space-y-1">
						<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Icon</span>
						<div ref={iconPickerRef} className="relative">
							<input
								type="text"
								value={form.icon}
								onChange={(event) => {
									update('icon', event.target.value);
									setIconPickerOpen(true);
								}}
								onFocus={() => setIconPickerOpen(true)}
								placeholder="Search icon name"
								className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
							/>
							{iconPickerOpen ? (
								<div className="absolute left-0 top-[calc(100%+4px)] z-20 max-h-56 w-[280px] overflow-auto rounded-md border border-slate-300 bg-white shadow-lg">
									{filteredIcons.length === 0 ? (
										<div className="px-2.5 py-2 text-xs text-slate-500">No icons found.</div>
									) : (
										filteredIcons.map((option) => {
											const IconComponent = option.Icon;
											return (
												<button
													type="button"
													key={option.value}
													onClick={() => {
														update('icon', option.value);
														setIconPickerOpen(false);
													}}
													className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-700 transition hover:bg-slate-100"
												>
													<IconComponent fontSize="small" />
													<span>{option.value}</span>
												</button>
											);
										})
									)}
								</div>
							) : null}
						</div>
					</label>

					<label className="space-y-1">
						<span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Background</span>
						<input
							type="text"
							value={form.backgroundColor}
							onChange={(event) => update('backgroundColor', event.target.value)}
							className="h-8 w-full rounded-md border border-slate-300 px-2.5 text-xs text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
						/>
					</label>
				</div>
			</div>
		</div>
	);
}

export default ReportWidgetEdit;
